import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from './common/Button';
import { LoadingSpinner } from './common/LoadingSpinner';
import { theme } from '../styles/theme';
import { apiEndpoint } from '../config';

const DemoContainer = styled.div`
  padding: ${theme.spacing['2xl']};
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  color: ${theme.colors.text.primary};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const PromptInput = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  font-family: ${theme.fonts.body};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ExampleButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
  margin-top: ${theme.spacing.md};
`;

const ExampleButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  cursor: pointer;
  font-size: ${theme.fontSizes.sm};
  
  &:hover {
    background: ${theme.colors.primary};
    color: white;
  }
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.xl};
  margin-top: ${theme.spacing.xl};
  
  @media (min-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ComparisonCard = styled.div<{ $isWinner?: boolean }>`
  background: ${theme.colors.background};
  border: 2px solid ${props => props.$isWinner ? '#10b981' : theme.colors.border};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.xl};
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const CardTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const PromptText = styled.div`
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.md};
  font-family: 'Monaco', monospace;
  font-size: ${theme.fontSizes.sm};
  line-height: 1.6;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
`;

const ResponseText = styled.div`
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.borderLight};
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.6;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
`;

const SectionLabel = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${theme.spacing.xs};
`;

const Score = styled.div`
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ScoreLabel = styled.span`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const ScoreValue = styled.span`
  font-size: ${theme.fontSizes.xl};
  font-weight: bold;
  color: ${theme.colors.primary};
`;

const DomainSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.sm};
`;

const DomainLabel = styled.span`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.muted};
  font-weight: 500;
`;

const DomainSelect = styled.select`
  background: ${theme.colors.backgroundSecondary};
  color: ${theme.colors.text.primary};
  padding: 2px ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  text-transform: capitalize;
  cursor: pointer;
  
  &:hover {
    border-color: ${theme.colors.primary};
  }
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const NoDomainBadge = styled.span`
  display: inline-block;
  background: ${theme.colors.backgroundSecondary};
  color: ${theme.colors.text.muted};
  padding: 2px ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.xs};
  font-style: italic;
`;

const ErrorMessage = styled.div<{ $isWarning?: boolean }>`
  background: ${props => props.$isWarning ? '#fffbeb' : '#fee'};
  border: 1px solid ${props => props.$isWarning ? '#f59e0b' : '#f44'};
  color: ${props => props.$isWarning ? '#92400e' : '#c00'};
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.md};
  margin-top: ${theme.spacing.md};
`;

const ModeSelector = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const ModeButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
`;

const ModeButton = styled.button<{ $active: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background: ${props => props.$active ? theme.colors.primary : 'white'};
  color: ${props => props.$active ? 'white' : theme.colors.text.primary};
  border: 2px solid ${props => props.$active ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.md};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    border-color: ${theme.colors.primary};
    background: ${props => props.$active ? theme.colors.primaryDark : theme.colors.backgroundSecondary};
  }
`;

const Badge = styled.span<{ $type: 'recommended' | 'advanced' }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  background: ${props => props.$type === 'recommended' ? '#10b981' : '#6366f1'};
  color: white;
  margin-left: ${theme.spacing.sm};
`;

const InfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  background: ${theme.colors.primaryLight};
  border-radius: ${theme.radii.md};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const ApiKeyInput = styled.input`
  flex: 1;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-family: monospace;
  margin-top: ${theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const RateLimitInfo = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.xs};
  margin-top: ${theme.spacing.sm};
`;

const ResponseMetadata = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.muted};
  padding-top: ${theme.spacing.xs};
  border-top: 1px solid ${theme.colors.borderLight};
  margin-top: ${theme.spacing.sm};
`;

interface SimpleDemoComparisonProps {
  showResponsesOnly?: boolean;
}

export const SimpleDemoComparison: React.FC<SimpleDemoComparisonProps> = ({ showResponsesOnly = true }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');
  const [demoMode, setDemoMode] = useState<'real' | 'custom'>('real');
  const [customApiKey, setCustomApiKey] = useState('');
  const [remainingCalls, setRemainingCalls] = useState<number | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [availableDomains] = useState([
    'general', 'screenwriting', 'creative_writing', 'business', 
    'business_strategy', 'technical', 'academic', 'marketing', 'programming'
  ]);
  const [regeneratingFossil, setRegeneratingFossil] = useState(false);

  // Fetch remaining demo calls on mount
  React.useEffect(() => {
    const fetchRemaining = async () => {
      try {
        const response = await fetch(apiEndpoint('/api/demo/remaining'));
        if (response.ok) {
          const data = await response.json();
          setRemainingCalls(data.remaining_calls);
        }
      } catch (err) {
        // Ignore errors, just don't show remaining calls
      }
    };
    fetchRemaining();
  }, []);

  const examples = [
    "Write a pitch for investors",
    "Create a scene between Don and Peggy from Mad Men",
    "Explain binary search",
    "Plan a trip to Japan"
  ];

  const runComparison = async () => {
    if (!prompt.trim()) return;
    
    setLoading(true);
    setError('');
    setResults(null);
    
    try {
      // Determine which provider and API key to use
      let llmProvider = 'openai';
      let apiKey = undefined;
      
      if (demoMode === 'real') {
        // API key will be handled server-side for demo mode
        apiKey = undefined;
      } else if (demoMode === 'custom' && customApiKey) {
        apiKey = customApiKey;
      } else if (demoMode === 'custom' && !customApiKey) {
        setError('Please enter your API key');
        setLoading(false);
        return;
      }
      
      const response = await fetch(apiEndpoint('/api/demo/compare'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          improvement_type: 'both',
          llm_provider: llmProvider,
          api_key: apiKey,
          user_tier: 'raptor',
          use_demo_key: demoMode === 'real' // Tell backend to use our key
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed: ${response.statusText}`);
      }
      
      const data = await response.json();
      setResults(data);
      
      // Extract domain from Fossil Fills result
      const fossilFillsResult = data.improved_prompts?.find((p: any) => p.version_type === 'fossil_fills');
      if (fossilFillsResult?.metadata?.domain) {
        setSelectedDomain(fossilFillsResult.metadata.domain);
      }
      
      // Check if we're using demo keys and they're available
      if (demoMode === 'real' && !data.llm_responses?.length) {
        setError('Unable to get AI responses. Demo API keys may not be configured.');
      }
      
      // Update remaining calls if using demo mode
      if (demoMode === 'real' && data.remaining_demo_calls !== undefined) {
        setRemainingCalls(data.remaining_demo_calls);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to run comparison');
    } finally {
      setLoading(false);
    }
  };

  const getScore = (version: string) => {
    if (!results?.evaluations) return 0;
    const index = version === 'original' ? 0 : 
                  version === 'promptasaurus' ? 1 : 2;
    return results.evaluations[index]?.overall_score || 0;
  };

  const getWinner = () => {
    if (!results) return null;
    const scores = {
      original: getScore('original'),
      promptasaurus: getScore('promptasaurus'),
      fossil_fills: getScore('fossil_fills')
    };
    return Object.entries(scores).reduce((a, b) => a[1] > b[1] ? a : b)[0];
  };

  const handleDomainChange = async (newDomain: string) => {
    if (!results || regeneratingFossil) return;
    
    setRegeneratingFossil(true);
    setError('');
    
    try {
      // Re-run just the Fossil Fills with the new domain
      const requestBody = {
        prompt,
        improvement_type: 'fossil_fills',
        domain: newDomain,  // Specify the domain
        llm_provider: demoMode === 'custom' && customApiKey ? 'openai' : 'openai',
        api_key: demoMode === 'custom' ? customApiKey : undefined,
        use_demo_key: demoMode === 'real',
        user_tier: 'free'
      };
      
      const response = await fetch(apiEndpoint('/api/demo/compare'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update just the Fossil Fills result
      setResults((prev: any) => ({
        ...prev,
        improved_prompts: prev.improved_prompts.map((p: any) => 
          p.version_type === 'fossil_fills' ? data.improved_prompts[0] : p
        ),
        llm_responses: showResponsesOnly ? [
          prev.llm_responses[0], // Keep original
          prev.llm_responses[1], // Keep promptasaurus
          data.llm_responses?.[1] || prev.llm_responses[2] // Update fossil fills response
        ] : prev.llm_responses,
        evaluations: data.evaluations ? [
          prev.evaluations[0], // Keep original
          prev.evaluations[1], // Keep promptasaurus
          data.evaluations[2]  // Update fossil fills evaluation
        ] : prev.evaluations
      }));
      
      setSelectedDomain(newDomain);
    } catch (err: any) {
      setError(`Failed to regenerate with new domain: ${err.message}`);
    } finally {
      setRegeneratingFossil(false);
    }
  };

  return (
    <DemoContainer>
      <Title>🔍 {showResponsesOnly ? 'Prompt & Response Comparison' : 'Prompt Comparison'}</Title>
      
      <ModeSelector>
        <ModeButtons>
          <ModeButton 
            $active={demoMode === 'real'} 
            onClick={() => setDemoMode('real')}
          >
            ✨ Demo API
            <Badge $type="recommended">Free - 10 calls/day</Badge>
          </ModeButton>
          <ModeButton 
            $active={demoMode === 'custom'} 
            onClick={() => setDemoMode('custom')}
          >
            🔑 Your API Key
            <Badge $type="advanced">Unlimited</Badge>
          </ModeButton>
        </ModeButtons>
        
        {demoMode === 'real' && (
          <>
            <InfoBox>
              <span>⚡</span>
              <span>Using real OpenAI GPT-3.5 API - See actual AI improvements in action</span>
            </InfoBox>
            {remainingCalls !== null && (
              <RateLimitInfo>
                {remainingCalls > 0 
                  ? `${remainingCalls} demo calls remaining today`
                  : 'Daily demo limit reached. Try mock mode or use your own API key.'}
              </RateLimitInfo>
            )}
          </>
        )}
        
        {demoMode === 'custom' && (
          <>
            <InfoBox>
              <span>🔐</span>
              <span>Enter your OpenAI API key for unlimited comparisons</span>
            </InfoBox>
            <ApiKeyInput
              type="password"
              placeholder="sk-..."
              value={customApiKey}
              onChange={(e) => setCustomApiKey(e.target.value)}
            />
            <RateLimitInfo>
              Your API key is sent directly to OpenAI and not stored. <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">Get an API key</a>
            </RateLimitInfo>
          </>
        )}
      </ModeSelector>
      
      <PromptInput>
        <TextArea
          placeholder="Enter your prompt here..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <ExampleButtons>
          <span>Try:</span>
          {examples.map((ex) => (
            <ExampleButton key={ex} onClick={() => setPrompt(ex)}>
              {ex}
            </ExampleButton>
          ))}
        </ExampleButtons>
      </PromptInput>
      
      <Button 
        onClick={runComparison} 
        disabled={
          loading || 
          !prompt.trim() || 
          (demoMode === 'real' && remainingCalls === 0) ||
          (demoMode === 'custom' && !customApiKey)
        }
      >
        {loading ? 'Comparing...' : 'Compare Prompts'}
      </Button>
      
      {error && (
        <ErrorMessage $isWarning={false}>
          {error}
        </ErrorMessage>
      )}
      
      {loading && <LoadingSpinner text="Generating comparisons..." />}
      
      {results && (
        <>
          <ComparisonGrid>
            <ComparisonCard $isWinner={getWinner() === 'original'}>
              <CardTitle>📝 Original</CardTitle>
              
              <div>
                <SectionLabel>Prompt</SectionLabel>
                <PromptText>{results.original_prompt.prompt_text}</PromptText>
              </div>
              
              {showResponsesOnly && results.llm_responses && results.llm_responses[0] && (
                <div>
                  <SectionLabel>AI Response</SectionLabel>
                  <ResponseText style={{ 
                    color: results.llm_responses[0].error ? theme.colors.error : 'inherit',
                    borderColor: results.llm_responses[0].error ? theme.colors.error : theme.colors.borderLight
                  }}>
                    {results.llm_responses[0].response_text}
                  </ResponseText>
                  <ResponseMetadata>
                    <span>{results.llm_responses[0].model_used}</span>
                    <span>{results.llm_responses[0].response_time_ms}ms</span>
                    {results.llm_responses[0].error && <span style={{color: theme.colors.error}}>⚠️ API Error</span>}
                  </ResponseMetadata>
                </div>
              )}
              
              <Score>
                <ScoreLabel>Score</ScoreLabel>
                <ScoreValue>{(getScore('original') * 100).toFixed(0)}%</ScoreValue>
              </Score>
            </ComparisonCard>
            
            {results.improved_prompts.map((improved: any, index: number) => {
              const responseIndex = index + 1; // Skip original response
              const response = results.llm_responses?.[responseIndex];
              
              return (
                <ComparisonCard 
                  key={improved.version_type}
                  $isWinner={getWinner() === improved.version_type}
                  style={{ opacity: regeneratingFossil && improved.version_type === 'fossil_fills' ? 0.6 : 1 }}
                >
                  <CardTitle>
                    {improved.version_type === 'promptasaurus' ? '✨ Promptasaurus' : '🦕 Fossil Fills'}
                  </CardTitle>
                  
                  {/* Domain selector for Fossil Fills, indicator for Promptasaurus */}
                  {improved.version_type === 'fossil_fills' ? (
                    <DomainSection>
                      <DomainLabel>Domain:</DomainLabel>
                      <DomainSelect 
                        value={selectedDomain || improved.metadata?.domain || 'general'}
                        onChange={(e) => handleDomainChange(e.target.value)}
                        disabled={regeneratingFossil}
                      >
                        {availableDomains.map(domain => (
                          <option key={domain} value={domain}>
                            {domain.replace('_', ' ')}
                          </option>
                        ))}
                      </DomainSelect>
                      {regeneratingFossil && <span style={{ fontSize: '12px' }}>Regenerating...</span>}
                    </DomainSection>
                  ) : improved.version_type === 'promptasaurus' ? (
                    <DomainSection>
                      <NoDomainBadge>No domain detection</NoDomainBadge>
                    </DomainSection>
                  ) : null}
                  
                  <div>
                    <SectionLabel>Improved Prompt</SectionLabel>
                    <PromptText>{improved.prompt_text}</PromptText>
                  </div>
                  
                  {showResponsesOnly && response && (
                    <div>
                      <SectionLabel>AI Response</SectionLabel>
                      <ResponseText style={{ 
                        color: response.error ? theme.colors.error : 'inherit',
                        borderColor: response.error ? theme.colors.error : theme.colors.borderLight
                      }}>
                        {response.response_text}
                      </ResponseText>
                      <ResponseMetadata>
                        <span>{response.model_used}</span>
                        <span>{response.response_time_ms}ms</span>
                        {response.error && <span style={{color: theme.colors.error}}>⚠️ API Error</span>}
                      </ResponseMetadata>
                    </div>
                  )}
                  
                  <Score>
                    <ScoreLabel>Score</ScoreLabel>
                    <ScoreValue>{(getScore(improved.version_type) * 100).toFixed(0)}%</ScoreValue>
                  </Score>
                </ComparisonCard>
              );
            })}
          </ComparisonGrid>
          
          {results.comparison_summary && (
            <div style={{ marginTop: theme.spacing['2xl'], padding: theme.spacing.xl, 
                         background: theme.colors.backgroundSecondary, 
                         borderRadius: theme.radii.lg }}>
              <h3>Analysis</h3>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: theme.fonts.body }}>
                {results.comparison_summary}
              </pre>
            </div>
          )}
        </>
      )}
    </DemoContainer>
  );
};